import { Badge } from '../ui/badge';

/**
 * Display the *current effective amount* charged to a customer for any paid
 * entity (lane booking, tournament registration, enrollment, camp,
 * coaching session, etc.). When a refund has been issued, this shows the
 * net amount in primary type and a small "−$X refunded" pill next to it.
 *
 * The data model is unified across all 5 supported entity types — every one
 * has the same `refunded_amount` and `refund_status` fields — so this single
 * component covers every "money" surface in the app.
 *
 * Props:
 *   amount         — original/full amount charged (price, payment_amount,
 *                    total_amount, etc.). Required.
 *   refundedAmount — total refunded so far (defaults to 0).
 *   refundStatus   — 'partially_refunded' | 'fully_refunded' | other.
 *   className      — optional wrapper class.
 *   amountClassName — optional class on the primary number.
 *   currency       — currency symbol, default '$'.
 *   testId         — optional data-testid prefix.
 *   inline         — if true, render the pill inline; if false, on a new
 *                    line below (better for tight tables).
 *
 * Usage:
 *   <NetAmount amount={booking.price} refundedAmount={booking.refunded_amount} refundStatus={booking.refund_status} />
 */
export const NetAmount = ({
  amount,
  refundedAmount = 0,
  refundStatus,
  className = '',
  amountClassName = '',
  currency = '$',
  testId,
  inline = true,
}) => {
  const orig = Number(amount || 0);
  const refunded = Number(refundedAmount || 0);
  const hasRefund = refunded > 0.005;
  const isFully = refundStatus === 'fully_refunded' || refunded + 0.005 >= orig && hasRefund;
  const net = Math.max(0, orig - refunded);

  return (
    <span className={`${inline ? 'inline-flex' : 'flex flex-col gap-0.5'} items-baseline gap-1.5 ${className}`} data-testid={testId}>
      <span className={amountClassName} data-testid={testId ? `${testId}-amount` : undefined}>
        {currency}{net.toFixed(2)}
      </span>
      {hasRefund && (
        <Badge
          variant="outline"
          className={`text-[10px] px-1.5 py-0 leading-none h-4 ${
            isFully
              ? 'bg-rose-50 text-rose-700 border-rose-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'
          }`}
          data-testid={testId ? `${testId}-refund-pill` : 'net-amount-refund-pill'}
          title={`Original: ${currency}${orig.toFixed(2)} · Refunded: ${currency}${refunded.toFixed(2)}`}
        >
          {isFully ? `Fully refunded (${currency}${refunded.toFixed(2)})` : `−${currency}${refunded.toFixed(2)} refunded`}
        </Badge>
      )}
    </span>
  );
};

/** Pure helper if you only need the net number (no JSX). */
export const computeNetAmount = (amount, refundedAmount = 0) =>
  Math.max(0, Number(amount || 0) - Number(refundedAmount || 0));

export default NetAmount;
